import {
  Component,
  ContentChildren,
  QueryList,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  AfterViewInit,
  AfterContentChecked,
  OnDestroy,
  ElementRef,
  ContentChild,
  ChangeDetectorRef,
  ViewChild,
  ViewContainerRef,
  TemplateRef,
} from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
import { Subscription } from "rxjs";
import { trigger, style, transition, animate, state } from "@angular/animations";
import { DsOptionComponent } from "../ds-selector-components/ds-option/ds-option.component";
import { isEqual } from "lodash-es";
import { DsDropdownTriggerComponent } from "../ds-selector-components/ds-dropdown-trigger/ds-dropdown-trigger.component";
import { DsOptionService } from "../../services/ds-option.service";
import { AnimationStates } from "../types/tooltips.types";
import { ConnectedPosition, Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";

@Component({
  selector: "ds-dropdown",
  templateUrl: "./ds-dropdown.component.html",
  styleUrls: ["./ds-dropdown.component.scss"],
  animations: [
    trigger("fadeInOut", [
      state(
        "hidden",
        style({
          opacity: 0,
          transform: "scale(0.8) translateY(-8px)",
        })
      ),
      state(
        "visible",
        style({
          opacity: 1,
          transform: "scale(1) translateY(0)",
        })
      ),
      transition("hidden => visible", [animate("300ms cubic-bezier(0.25, 0.8, 0.25, 1)")]),
      transition("visible => hidden", [animate("300ms cubic-bezier(0.4, 0, 0.2, 1)")]),
    ]),
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DsDropdownComponent),
      multi: true,
    },
  ],
})
export class DsDropdownComponent implements ControlValueAccessor, AfterViewInit, AfterContentChecked, OnDestroy {
  @Input() disabled = false;
  @Input() placeholder = "";
  @Input() error = false;
  @Input() multiple = false;
  @Input() label = "";
  @Input() hintText = "";
  @Input() required = false;
  @Input() emptyOptionsText = "Sin opciones";
  /** Force panel to open below ('below'), above ('above'), or auto (default). Not used when panelFitContent is true. */
  @Input() dropdownPosition: "below" | "above" | "auto" = "auto";
  @Input() panelFitContent = false;
  @Input() panelMaxWidth = "";
  @Input() panelHorizontalAlign: "start" | "end" = "end";

  /** Emits the CDK overlay pane element when the panel opens (useful for attaching scroll listeners). */
  @Output() opened = new EventEmitter<HTMLElement | null>();
  /** Emits when the panel closes (overlay detached or programmatic close). */
  @Output() closed = new EventEmitter<void>();

  @ContentChild(DsDropdownTriggerComponent) trigger!: DsDropdownTriggerComponent;
  @ContentChildren(DsOptionComponent, { descendants: true }) options!: QueryList<DsOptionComponent>;
  @ViewChild("dropdownElement", { static: false }) dropdownElement!: ElementRef<HTMLDivElement>;
  @ViewChild("optionListContent") optionListContent!: TemplateRef<any>;
  @ViewChild("selectedOptionContainer", { static: false }) selectedOptionContainer!: ElementRef<HTMLSpanElement>;

  isOpen = false;
  value: any[] | any = this.multiple ? [] : null;
  isAnimating = false;
  displayValue = "";
  highlightedIndex = -1;
  selectedOptions: DsOptionComponent[] = [];
  searchQuery = "";
  lastSearchIndex = -1;
  animationState: AnimationStates = "hidden";
  overlayRef: OverlayRef | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private previousTriggerState = false;
  private optionSubs: Subscription[] = [];
  private searchResetTimeout: ReturnType<typeof setTimeout> | null = null;
  // Keystrokes typed within this window build a single search, like a native select does.
  private readonly SEARCH_RESET_MS = 700;

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private vcr: ViewContainerRef,
    private dsOptionService: DsOptionService
  ) {}

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (!this.isOpen) {
      if (event.key === "Enter") {
        event.preventDefault();
        this.togglePanel();
      }
      return;
    }
    const enabledOptions = this.dsOptionService.getEnabledOptions(this.options);
    if (enabledOptions.length === 0) return;
    if (this.isSearchKey(event)) {
      event.preventDefault();
      this.searchOption(event.key);
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.highlightedIndex = this.highlightedIndex < enabledOptions.length - 1 ? this.highlightedIndex + 1 : 0;
        this.updateOptionsHighlight();
        this.scrollToHighlightedOption();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.highlightedIndex = this.highlightedIndex > 0 ? this.highlightedIndex - 1 : enabledOptions.length - 1;
        this.updateOptionsHighlight();
        this.scrollToHighlightedOption();
        break;
      case "Enter":
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < enabledOptions.length) {
          this.selectOption(enabledOptions[this.highlightedIndex]);
        }
        break;
      case "Escape":
        event.preventDefault();
        this.closePanel();
        break;
    }
  }

  private isSearchKey(event: KeyboardEvent): boolean {
    if (event.ctrlKey || event.altKey || event.metaKey || event.key.length !== 1) {
      return false;
    }
    // A leading space belongs to the panel, it only extends a search already in progress.
    return event.key !== " " || this.searchQuery.length > 0;
  }

  /**
   * Builds the search with every keystroke so typing "ma" reaches Mazda instead of jumping
   * back to the options starting with "a". Repeating one character walks through its matches.
   */
  private searchOption(key: string): void {
    const query = this.searchQuery + key;
    const isRepeatedCharacter = query.length > 1 && new Set(query.toLowerCase()).size === 1;
    const searchText = isRepeatedCharacter ? key : query;
    const startIndex = isRepeatedCharacter ? this.lastSearchIndex + 1 : 0;

    const foundIndex = this.dsOptionService.findOptionByText(this.options, searchText, startIndex);
    this.scheduleSearchReset();

    // Without a match the keystroke is dropped, so a typo does not discard the search.
    if (foundIndex < 0) {
      return;
    }

    this.searchQuery = query;
    this.lastSearchIndex = foundIndex;
    this.highlightedIndex = foundIndex;
    this.updateOptionsHighlight();
    this.scrollToHighlightedOption();
  }

  private scheduleSearchReset(): void {
    this.clearSearchTimeout();
    this.searchResetTimeout = setTimeout(() => this.resetSearch(), this.SEARCH_RESET_MS);
  }

  private resetSearch(): void {
    this.clearSearchTimeout();
    this.searchQuery = "";
    this.lastSearchIndex = -1;
  }

  private clearSearchTimeout(): void {
    if (this.searchResetTimeout) {
      clearTimeout(this.searchResetTimeout);
      this.searchResetTimeout = null;
    }
  }

  writeValue(value: any): void {
    this.value = value;
    this.updateOptionsSelection();
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngAfterViewInit() {
    if (!this.value && this.multiple) {
      this.value = [];
    }

    this.options.changes.subscribe(() => {
      this.setupOptionListeners();
      setTimeout(() => {
        this.updateOptionsSelection();
        this.cdr.detectChanges();
      });
    });
    this.setupOptionListeners();

    setTimeout(() => {
      this.updateOptionsSelection();
      this.cdr.detectChanges();
    });
  }

  /** Ensures every ds-option gets multiple/dropdown from parent (new options from *ngFor never received togglePanel). */
  private applyOptionParentInputs(): void {
    if (!this.options?.length) return;
    this.options.forEach((option) => {
      option.multiple = this.multiple;
      option.dropdown = true;
    });
  }

  ngAfterContentChecked() {
    const currentTriggerState = !!this.trigger;
    if (this.previousTriggerState !== currentTriggerState) {
      this.previousTriggerState = currentTriggerState;
      if (!currentTriggerState && this.value) {
        this.projectSelectedOption();
      }
    }
  }

  private setupOptionListeners() {
    this.optionSubs.forEach((s) => s.unsubscribe());
    this.optionSubs = [];

    this.applyOptionParentInputs();

    this.options.forEach((option) => {
      const sub = option.optionSelected.subscribe((selectedOption: DsOptionComponent) => {
        this.selectOption(selectedOption);
      });
      this.optionSubs.push(sub);
    });
  }

  togglePanel() {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.showOptions();
  }

  closePanel() {
    if (this.isOpen) {
      this.isOpen = false;
      this.highlightedIndex = -1;
      this.resetSearch();
      this.updateOptionsHighlight();
      this.hideOptions();
      this.onTouched();
    }
  }

  selectOption(option: DsOptionComponent) {
    if (this.multiple) {
      const exists = this.value?.find((v: any) => isEqual(v, option.value));
      this.value = exists ? this.value?.filter((v: any) => !isEqual(v, option.value)) : [...this.value, option.value];
      const enabledOptions = this.dsOptionService.getEnabledOptions(this.options);
      const newIndex = enabledOptions.indexOf(option);
      if (newIndex >= 0) {
        this.highlightedIndex = newIndex;
      }
    } else {
      this.value = option.value;
      this.isOpen = false;
      this.hideOptions();
      this.highlightedIndex = -1;
    }
    this.onChange(this.value);
    this.onTouched();
    this.updateOptionsSelection();
  }

  private updateOptionsSelection() {
    if (!this.options) return;

    this.options.forEach((opt) => {
      opt.selected = this.multiple
        ? this.value?.some((v: any) => isEqual(v, opt.value))
        : isEqual(this.value, opt.value);
    });

    this.displayValue = this.getDisplayValue();
    this.projectSelectedOption();
    if (this.isOpen) {
      this.updateOptionsHighlight();
    }
  }

  private projectSelectedOption(): void {
    if (this.trigger) {
      this.selectedOptions = [];
      return;
    }
    if (this.multiple) {
      this.selectedOptions = this.options?.filter((opt) => this.value?.some((v: any) => isEqual(v, opt.value))) || [];
    } else {
      const found = this.options?.find((opt) => isEqual(opt.value, this.value));
      this.selectedOptions = found ? [found] : [];
    }
    this.cdr.detectChanges();
    setTimeout(() => this.populateSelectedContent());
  }

  private populateSelectedContent(): void {
    if (!this.selectedOptionContainer?.nativeElement) return;

    this.selectedOptionContainer.nativeElement.innerHTML = "";

    this.selectedOptions.forEach((option, index) => {
      const optionElement = option.elementRefInstance.nativeElement;
      const labelElement = optionElement.querySelector(".option-label");
      if (labelElement) {
        const clone = labelElement.cloneNode(true) as HTMLElement;
        this.selectedOptionContainer.nativeElement.appendChild(clone);

        if (this.multiple && index < this.selectedOptions.length - 1) {
          const separator = document.createElement("span");
          separator.className = "selected-separator";
          separator.textContent = ", ";
          this.selectedOptionContainer.nativeElement.appendChild(separator);
        }
      }
    });
  }

  get isPlaceholder(): boolean {
    return (!this.value && !this.multiple) || (this.multiple && this.value?.length === 0);
  }

  get hasOptions(): boolean {
    if (!this.options) return false;
    const realOptions = this.options.filter((opt) => !opt.default);
    return realOptions.length > 0;
  }

  getDisplayValue(): string {
    if (this.isPlaceholder) {
      return this.placeholder || "";
    }

    const selectedOption = this.options?.find((option) => isEqual(option.value, this.value));

    if (this.multiple) {
      if (Array.isArray(this.value) && this.value?.length > 0) {
        const selectedOptions =
          this.options?.filter((option) => this.value.some((v: any) => isEqual(v, option.value))) || [];
        return selectedOptions.map((option) => option.displayText).join(", ");
      }
      return "";
    } else {
      return selectedOption?.displayText || this.value;
    }
  }

  private updateOptionsHighlight(): void {
    this.dsOptionService.updateOptionsHighlight(this.options, this.highlightedIndex);
  }

  private scrollToHighlightedOption(): void {
    const container = this.overlayRef?.overlayElement ?? this.elementRef.nativeElement;
    this.dsOptionService.scrollToHighlightedOption(this.options, this.highlightedIndex, container);
  }

  ngOnDestroy(): void {
    this.optionSubs.forEach((s) => s.unsubscribe());
    this.stopResizeObserver();
    this.clearSearchTimeout();
  }

  /**
   * Positions for fit-content panels. Uses only start or only end pairs: with flexible
   * dimensions the CDK scores all flexibleFits by bounding-box area × weight and can pick a
   * later position — mixing start and end caused "end" preference to lose to "start".
   */
  private getPanelFitContentPositions(offsetY = 4): ConnectedPosition[] {
    const startBelow: ConnectedPosition = {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY,
    };
    const startAbove: ConnectedPosition = {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -offsetY,
    };
    const endBelow: ConnectedPosition = {
      originX: "end",
      originY: "bottom",
      overlayX: "end",
      overlayY: "top",
      offsetY,
    };
    const endAbove: ConnectedPosition = {
      originX: "end",
      originY: "top",
      overlayX: "end",
      overlayY: "bottom",
      offsetY: -offsetY,
    };
    return this.panelHorizontalAlign === "end" ? [endBelow, endAbove] : [startBelow, startAbove];
  }

  showOptions = () => {
    if (this.overlayRef) return;
    this.isOpen = true;

    const element = this.dropdownElement.nativeElement;

    const positionOptions = this.panelFitContent
      ? {
          positions: this.getPanelFitContentPositions(4),
          ...(this.panelHorizontalAlign === "end" ? { flexibleDimensions: false, growAfterOpen: false } : {}),
        }
      : {
          positions: this.dsOptionService.getPositionConfigFor(this.dropdownPosition),
        };

    const positionStrategy = this.dsOptionService.createPositionStrategy(
      this.overlayPositionBuilder,
      element,
      positionOptions
    );

    this.overlayRef = this.dsOptionService.createOverlay(this.overlay, positionStrategy, element);

    if (this.panelFitContent) {
      const sizeConfig: { width: string; minWidth: number; maxWidth?: string } = {
        width: "auto",
        minWidth: element.offsetWidth,
      };
      if (this.panelMaxWidth) {
        sizeConfig.maxWidth = this.panelMaxWidth;
      }
      this.overlayRef.updateSize(sizeConfig);
    }

    this.dsOptionService.setupPositionChangeListener(positionStrategy, element, this.overlayRef);

    this.overlayRef.detachments().subscribe(() => this.onOverlayClosed());
    this.dsOptionService.attachTemplate(this.overlayRef, this.optionListContent, this.vcr);
    this.startResizeObserver();

    setTimeout(() => document.addEventListener("click", this.onDocumentClick));
    requestAnimationFrame(() => this.overlayRef?.updatePosition());
    // Ensure animation state is applied in the overlay's embedded view.
    this.animationState = "hidden";
    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.animationState = "visible";
      this.cdr.detectChanges();
    });

    this.opened.emit(this.overlayRef?.overlayElement ?? null);

    const enabledOptions = this.dsOptionService.getEnabledOptions(this.options);
    if (enabledOptions.length > 0) {
      const selectedIndex = enabledOptions.findIndex((opt) => opt.selected);
      this.highlightedIndex = selectedIndex >= 0 ? selectedIndex : 0;
      setTimeout(() => {
        this.updateOptionsHighlight();
        requestAnimationFrame(() => setTimeout(() => this.scrollToHighlightedOption(), 50));
        this.dropdownElement?.nativeElement.focus();
      }, 100);
    }
  };

  private onOverlayClosed() {
    document.removeEventListener("click", this.onDocumentClick);
    this.stopResizeObserver();
    this.animationState = "hidden";
    this.overlayRef = null;
  }

  hideOptions = () => {
    if (!this.overlayRef) return;
    this.closed.emit();
    this.isOpen = false;
    this.onTouched();
    document.removeEventListener("click", this.onDocumentClick);
    this.stopResizeObserver();
    this.animationState = "hidden";

    this.dsOptionService.disposeOverlay(this.overlayRef).then(() => (this.overlayRef = null));

    this.highlightedIndex = -1;
    this.resetSearch();
    this.updateOptionsHighlight();
  };

  private onDocumentClick = (event: MouseEvent) => {
    if (this.dsOptionService.isClickOutside(event, this.dropdownElement?.nativeElement, this.overlayRef)) {
      this.hideOptions();
    }
  };

  private startResizeObserver(): void {
    if (!this.overlayRef) return;

    if (this.panelFitContent) {
      const el = this.dropdownElement.nativeElement;
      const ref = this.overlayRef;
      this.resizeObserver = new ResizeObserver(() => {
        ref.updateSize({ minWidth: el.offsetWidth });
      });
      this.resizeObserver.observe(el);
    } else {
      this.resizeObserver = this.dsOptionService.createResizeObserver(
        this.dropdownElement.nativeElement,
        this.overlayRef
      );
    }
  }

  private stopResizeObserver(): void {
    this.resizeObserver = this.dsOptionService.stopResizeObserver(this.resizeObserver);
  }
}
